import React from 'react'
import { useTable, EditButton, DeleteButton, CreateButton } from '@refinedev/antd'
import { Space } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { ListPage, SectionCard, EmptyHint, type Stat } from '../../theme/listkit'
import { HandHeart } from '../../components/icons'

type Fund = Tables<'funds'>

/** A single fund: bordered card, Cinzel name, inline Edit/Delete (PK is `name`). */
function FundCard({ name }: { name: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        padding: '14px 16px',
        borderRadius: 12,
        background: '#fff',
        border: '1px solid ' + brand.cardLine,
        borderLeft: '3px solid ' + brand.gold,
      }}
    >
      <span
        style={{
          fontFamily: brand.fDisplay,
          fontWeight: 700,
          fontSize: 14,
          color: brand.ink,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </span>
      <Space size={2} style={{ flexShrink: 0 }}>
        <EditButton hideText size="small" recordItemId={name} />
        <DeleteButton hideText size="small" recordItemId={name} />
      </Space>
    </div>
  )
}

export function FundList() {
  // useTable establishes the `funds` resource context (so the intro <CreateButton/>
  // resolves) and loads the rows; we render our own chip grid instead of a table.
  // Funds are a small, domain-bounded set — cap the page at 200 (explicit bound
  // instead of mode:'off') so the whole designation list renders in one chip grid.
  const { tableProps } = useTable<Fund>({
    syncWithLocation: true,
    pagination: { pageSize: 200 },
  })
  const funds = (tableProps.dataSource ?? []) as Fund[]

  const stats: Stat[] = [{ label: 'Total Funds', value: funds.length.toLocaleString(), icon: HandHeart, tone: 'gold' }]

  return (
    <ListPage
      intro={{
        eyebrow: 'Giving · Designations',
        description:
          'The designations a gift can be directed to — Annual Fund, Scholarship, Athletics, and more. Every donation a member makes is earmarked to one of these funds.',
        actions: <CreateButton />,
      }}
      stats={stats}
      statColumns={3}
    >
      <SectionCard title="Designations" sub="Where alumni gifts are directed">
        {funds.length === 0 ? (
          <EmptyHint>No funds yet. Create the first designation to direct gifts.</EmptyHint>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 12,
            }}
          >
            {funds.map((fund) => (
              <FundCard key={String(fund.name)} name={fund.name} />
            ))}
          </div>
        )}
      </SectionCard>
    </ListPage>
  )
}
